import { PermissionCode } from '@gb-ticket/shared-types';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import { MasterDataListState } from '@/platform/features/master-data/master-data-list-state';
import {
  useCostCenters,
  useCreateCostCenter,
  usePatchCostCenter,
} from '@/platform/features/master-data/use-master-data';

export function CostCentersPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.MASTER_DATA_MANAGE);
  const centers = useCostCenters();

  return (
    <section className="space-y-6">
      <PlatformPageHeader title="Kostenstellen" description="Code und offene Bezeichnung." />
      <MasterDataListState
        pending={centers.isPending}
        pendingLabel="Kostenstellen werden geladen …"
        error={centers.isError ? centers.error : null}
        errorLabel="Die Kostenstellen konnten nicht geladen werden."
        empty={centers.data?.items.length === 0}
        emptyLabel="Noch keine Kostenstelle."
      >
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Status</TableHead>
                {canWrite ? <TableHead>Aktion</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {centers.data?.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-sm">{item.code}</TableCell>
                  <TableCell>
                    {item.versions.find((version) => version.validTo == null)?.name ?? '—'}
                    {item.versions.some((version) => version.validTo != null) ? (
                      <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                        {item.versions
                          .filter((version) => version.validTo != null)
                          .map((version) => (
                            <li key={version.validFrom}>
                              historisch · {version.name} · bis {version.validTo}
                            </li>
                          ))}
                      </ul>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {item.isActive ? (
                      <Badge variant="outline">Aktiv</Badge>
                    ) : (
                      <Badge variant="secondary">inaktiv</Badge>
                    )}
                  </TableCell>
                  {canWrite ? (
                    <TableCell>
                      {item.isActive ? <InactivateCostCenter id={item.id} /> : null}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </MasterDataListState>
      {canWrite ? <CostCenterForm /> : null}
    </section>
  );
}

function InactivateCostCenter({ id }: { id: string }) {
  const patch = usePatchCostCenter(id);

  async function inactivate() {
    try {
      await patch.mutateAsync({ isActive: false });
      toast.success('Kostenstelle inaktiviert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Kostenstelle konnte nicht inaktiviert werden.',
      );
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      disabled={patch.isPending}
      onClick={() => void inactivate()}
    >
      Inaktivieren
    </Button>
  );
}

function CostCenterForm() {
  const create = useCreateCostCenter();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [validFrom, setValidFrom] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({ code, name, validFrom });
      toast.success('Kostenstelle angelegt.');
      setCode('');
      setName('');
      setValidFrom('');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Kostenstelle konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form className="max-w-xl space-y-4" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div className="space-y-2">
        <Label htmlFor="center-code">Code</Label>
        <Input id="center-code" value={code} onChange={(event) => setCode(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="center-name">Bezeichnung</Label>
        <Input id="center-name" value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="center-from">Gültig ab</Label>
        <Input
          id="center-from"
          placeholder="JJJJ-MM-TT"
          value={validFrom}
          onChange={(event) => setValidFrom(event.target.value)}
        />
      </div>
      <Button type="submit" disabled={create.isPending}>
        Kostenstelle anlegen
      </Button>
    </form>
  );
}
